/**
 * 테이블별 그리드 컬럼 정의.
 *
 * label은 field-labels.ts 의 labelFor() 가 자동 처리하므로 여기서는 key/type/w 만.
 * 셀 폭은 가독성 기준의 권장치 — 실제로는 brower가 알아서 늘려줌.
 *
 * 백엔드 ADMIN_TABLES 와 1:1 매칭되어야 함.
 */

import type { GridColumn, TableGridDef } from '../types';

const c = (key: string, type: GridColumn['type'], w?: number): GridColumn => ({
  key,
  label: key, // 실제 라벨은 labelFor() 로 한글 변환
  type,
  w,
});

export const TABLE_META: Record<string, TableGridDef> = {
  chassis: {
    cols: [
      c('id', 'num', 48),
      c('option_part', 'mono', 130),
      c('vendor', 'chip', 80),
      c('model', 'text', 220),
      c('generation', 'chip', 80),
      c('form_factor', 'text', 110),
      c('rack_unit', 'num', 50),
      c('max_cpu_sockets', 'num', 70),
      c('memory_dimm_slots', 'num', 80),
      c('pcie_total_slots', 'num', 70),
      c('sku_type', 'chip', 90),
    ],
  },
  // 자식 테이블 첫 컬럼은 id(행 번호) + chassis_part(섀시 SKU) 순서.
  // chassis_id 는 v_* 뷰에 여전히 노출되지만 UI에서는 chassis_part 만 표시.
  chassis_storage_bay: {
    cols: [
      c('id', 'num', 48),
      c('chassis_part', 'mono', 130),
      c('bay_position', 'text', 140),
      c('bay_type', 'chip', 90),
      c('bay_count', 'num', 80),
      c('is_default', 'bool', 80),
      c('bay_raw', 'text', 220),
      c('notes', 'text', 220),
    ],
  },
  chassis_pcie_slot: {
    cols: [
      c('id', 'num', 48),
      c('chassis_part', 'mono', 130),
      c('slot_number', 'num', 70),
      c('riser_position', 'chip', 120),
      c('pcie_generation', 'num', 70),
      c('bus_width', 'num', 80),
      c('connector_width', 'num', 80),
      c('form_factor', 'text', 130),
      c('requires_proc', 'num', 90),
      c('is_default', 'bool', 80),
      c('riser_part', 'mono', 120),
    ],
  },
  chassis_ocp_slot: {
    cols: [
      c('id', 'num', 48),
      c('chassis_part', 'mono', 130),
      c('slot_number', 'num', 70),
      c('ocp_version', 'chip', 90),
      c('bus_width', 'num', 80),
      c('requires_proc', 'num', 90),
      c('notes', 'text', 240),
    ],
  },
  chassis_compatible_parts: {
    cols: [
      c('id', 'num', 48),
      c('chassis_part', 'mono', 130),
      c('category', 'chip', 140),
      c('option_part', 'mono', 130),
      c('required_or_optional', 'chip', 110),
      c('min_qty', 'num', 70),
      c('max_qty', 'num', 70),
      c('compatibility_notes', 'text', 240),
    ],
  },
  chassis_spec_event: {
    cols: [
      c('id', 'num', 48),
      c('chassis_part', 'mono', 130),
      c('quickspec_version', 'chip', 110),
      c('valid_from', 'date', 110),
      c('description', 'text', 360),
    ],
  },

  cpu: {
    cols: [
      c('id', 'num', 48),
      c('option_part', 'mono', 130),
      c('vendor', 'chip', 80),
      c('family', 'text', 160),
      c('model', 'text', 140),
      c('cores', 'num', 60),
      c('threads', 'num', 60),
      c('base_clock_ghz', 'num', 80),
      c('turbo_clock_ghz', 'num', 80),
      c('tdp_w', 'num', 70),
      c('generation', 'chip', 70),
      c('oem_vendor', 'chip', 90),
    ],
  },
  cpu_model: {
    cols: [
      c('id', 'num', 48),
      c('vendor', 'chip', 80),
      c('family', 'text', 160),
      c('model', 'text', 140),
      c('codename', 'text', 140),
      c('cores', 'num', 70),
      c('threads', 'num', 80),
      c('base_clock_ghz', 'num', 80),
      c('turbo_clock_ghz', 'num', 80),
      c('tdp_w', 'num', 70),
      c('memory_type', 'chip', 90),
      c('pcie_max_gen', 'num', 70),
    ],
  },
  gpu: {
    cols: [
      c('id', 'num', 48),
      c('option_part', 'mono', 130),
      c('vendor', 'chip', 90),
      c('family', 'text', 130),
      c('model', 'text', 160),
      c('memory_gb', 'num', 90),
      c('tdp_w_max', 'num', 80),
      c('fp32_tflops', 'num', 90),
      c('form_factor', 'chip', 90),
      c('oem_vendor', 'chip', 90),
    ],
  },
  gpu_model: {
    cols: [
      c('id', 'num', 48),
      c('vendor', 'chip', 90),
      c('family', 'text', 130),
      c('model', 'text', 160),
      c('architecture', 'text', 130),
      c('memory_gb', 'num', 90),
      c('memory_type', 'chip', 100),
      c('tdp_w_max', 'num', 80),
      c('fp32_tflops', 'num', 90),
      c('fp16_tflops', 'num', 90),
      c('supports_nvlink', 'bool', 80),
    ],
  },

  memory: {
    cols: [
      c('id', 'num', 48),
      c('option_part', 'mono', 130),
      c('vendor', 'chip', 80),
      c('memory_type', 'chip', 90),
      c('form_factor', 'chip', 110),
      c('capacity_gb', 'num', 80),
      c('speed_mts', 'num', 90),
      c('rank_count', 'num', 70),
      c('dram_width', 'chip', 80),
      c('voltage_v', 'num', 70),
      c('is_smart_memory', 'bool', 90),
    ],
  },
  ssd: {
    cols: [
      c('id', 'num', 48),
      c('option_part', 'mono', 130),
      c('vendor', 'chip', 80),
      c('interface', 'chip', 90),
      c('form_factor', 'chip', 90),
      c('capacity_gb', 'num', 90),
      c('workload_class', 'chip', 130),
      c('endurance_dwpd', 'num', 80),
      c('seq_read_mib_s', 'num', 100),
      c('seq_write_mib_s', 'num', 100),
      c('is_sed', 'bool', 70),
    ],
  },
  hdd: {
    cols: [
      c('id', 'num', 48),
      c('option_part', 'mono', 130),
      c('vendor', 'chip', 80),
      c('interface', 'chip', 90),
      c('form_factor', 'chip', 90),
      c('capacity_gb', 'num', 90),
      c('rpm', 'num', 80),
      c('workload_class', 'chip', 150),
      c('is_helium', 'bool', 70),
      c('is_sed', 'bool', 70),
    ],
  },

  network_adapter: {
    cols: [
      c('id', 'num', 48),
      c('option_part', 'mono', 130),
      c('vendor', 'chip', 110),
      c('chip_model', 'text', 180),
      c('interface_type', 'chip', 110),
      c('speed_gbps_max', 'num', 110),
      c('port_count', 'num', 80),
      c('pcie_gen', 'num', 70),
      c('card_profile', 'chip', 110),
      c('oem_vendor', 'chip', 90),
    ],
  },
  network_adapter_model: {
    cols: [
      c('id', 'num', 48),
      c('vendor', 'chip', 110),
      c('chip_model', 'text', 180),
      c('asic_model', 'text', 130),
      c('interface_type', 'chip', 110),
      c('speed_gbps_max', 'num', 100),
      c('port_count', 'num', 80),
      c('pcie_gen', 'num', 70),
      c('rdma_protocols', 'array', 160),
      c('tunnel_offload', 'array', 170),
      c('supports_sriov', 'bool', 90),
    ],
  },
  hba: {
    cols: [
      c('id', 'num', 48),
      c('option_part', 'mono', 130),
      c('vendor', 'chip', 100),
      c('series', 'text', 130),
      c('chip_family', 'text', 200),
      c('speed_gbps_max', 'num', 90),
      c('port_count', 'num', 80),
      c('bracket_type', 'chip', 140),
      c('includes_sfp', 'bool', 70),
      c('oem_vendor', 'chip', 90),
    ],
  },
  hba_model: {
    cols: [
      c('id', 'num', 48),
      c('vendor', 'chip', 100),
      c('series', 'text', 130),
      c('chip_family', 'text', 220),
      c('speed_gbps_max', 'num', 100),
      c('pcie_gen', 'num', 70),
      c('fc_protocols', 'array', 160),
      c('supports_smart_san', 'bool', 100),
      c('supports_fec', 'bool', 80),
    ],
  },
  raid_controller: {
    cols: [
      c('id', 'num', 48),
      c('option_part', 'mono', 130),
      c('oem_vendor', 'chip', 90),
      c('model', 'text', 170),
      c('type', 'chip', 120),
      c('chip_model', 'text', 130),
      c('cache_size_gb', 'num', 100),
      c('raid_levels', 'array', 180),
      c('max_iops_read', 'num', 110),
      c('energy_pack_required', 'bool', 110),
    ],
  },
  cache_battery: {
    cols: [
      c('id', 'num', 48),
      c('option_part', 'mono', 130),
      c('vendor', 'chip', 80),
      c('type', 'chip', 110),
      c('product_line', 'text', 180),
      c('cable_length_mm', 'num', 100),
      c('capacity_w', 'num', 100),
      c('max_joules', 'num', 90),
      c('supports_nvdimm', 'bool', 90),
    ],
  },

  power_supply: {
    cols: [
      c('id', 'num', 48),
      c('option_part', 'mono', 130),
      c('vendor', 'chip', 80),
      c('series', 'text', 140),
      c('input_type', 'chip', 110),
      c('output_wattage_max', 'num', 100),
      c('efficiency_rating', 'chip', 110),
      c('efficiency_pct', 'num', 80),
      c('is_hot_plug', 'bool', 90),
    ],
  },
  riser: {
    cols: [
      c('id', 'num', 48),
      c('option_part', 'mono', 130),
      c('vendor', 'chip', 80),
      c('chassis_model', 'text', 160),
      c('position', 'chip', 130),
      c('is_nebs_compliant', 'bool', 80),
      c('requires_cpu2', 'bool', 100),
      c('requires_cable_kit', 'mono', 130),
    ],
  },
  rail_kit: {
    cols: [
      c('id', 'num', 48),
      c('option_part', 'mono', 130),
      c('vendor', 'chip', 80),
      c('chassis_model', 'text', 160),
      c('generation', 'chip', 90),
      c('rail_type', 'chip', 130),
      c('server_form_factor', 'num', 60),
      c('supports_4post_rack', 'bool', 90),
      c('supports_2post_rack', 'bool', 90),
    ],
  },
  drive_tray: {
    cols: [
      c('id', 'num', 48),
      c('option_part', 'mono', 130),
      c('vendor', 'chip', 80),
      c('carrier_type', 'chip', 100),
      c('form_factor', 'chip', 100),
      c('generation', 'chip', 90),
      c('supports_sas', 'bool', 70),
      c('supports_sata', 'bool', 70),
      c('supports_nvme', 'bool', 70),
      c('is_hot_plug', 'bool', 90),
    ],
  },
  heatsink: {
    cols: [
      c('id', 'num', 48),
      c('option_part', 'mono', 130),
      c('vendor', 'chip', 80),
      c('type', 'chip', 140),
      c('chassis_model', 'text', 160),
      c('generation', 'chip', 90),
      c('min_tdp_w', 'num', 90),
      c('max_tdp_w', 'num', 90),
      c('is_low_profile', 'bool', 80),
      c('requires_dlc_module', 'bool', 80),
    ],
  },
  fan: {
    cols: [
      c('id', 'num', 48),
      c('option_part', 'mono', 130),
      c('vendor', 'chip', 80),
      c('fan_type', 'chip', 140),
      c('chassis_model', 'text', 160),
      c('generation', 'chip', 90),
      c('chassis_default_count', 'num', 100),
      c('chassis_max_count', 'num', 90),
      c('is_redundant', 'bool', 100),
    ],
  },
  accessory: {
    cols: [
      c('id', 'num', 48),
      c('option_part', 'mono', 130),
      c('vendor', 'chip', 80),
      c('type', 'chip', 130),
      c('parent_category', 'chip', 130),
      c('parent_part', 'mono', 130),
      c('chassis_model', 'text', 160),
      c('min_server_generation', 'num', 100),
    ],
  },

  pre_configured_model: {
    cols: [
      c('id', 'num', 48),
      c('option_part', 'mono', 130),
      c('vendor', 'chip', 80),
      c('config_type', 'chip', 130),
      c('base_chassis_id', 'num', 110),
      c('cpu_part', 'mono', 120),
      c('cpu_count', 'num', 70),
      c('memory_part', 'mono', 120),
      c('memory_capacity_gb', 'num', 90),
      c('drive_part', 'mono', 120),
      c('region_code', 'chip', 90),
    ],
  },
  seed_conflict_log: {
    cols: [
      c('id', 'num', 48),
      c('table_name', 'chip', 130),
      c('option_part', 'mono', 130),
      c('column_name', 'mono', 130),
      c('old_value', 'text', 160),
      c('new_value', 'text', 160),
      c('chassis_source', 'chip', 130),
      c('quickspec_version', 'chip', 110),
      c('detected_at', 'datetime', 160),
      c('resolved', 'bool', 80),
      c('resolution', 'chip', 130),
    ],
  },
};
